import React, { useState, createContext, useContext } from 'react';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';

// Context for managing the dialog globally
const ConfirmDialogContext = createContext();

// Hook to use the confirm dialog
export const useConfirmDialog = () => {
    const context = useContext(ConfirmDialogContext);
    if (!context) {
        throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
    }
    return context;
};

// Provider component
export const ConfirmDialogProvider = ({ children }) => {
    const [dialog, setDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'info',
        onConfirm: null,
        onCancel: null,
        confirmText: 'Confirm',
        cancelText: 'Cancel',
    });

    const confirm = ({
        title = 'Confirm Action',
        message,
        type = 'info',
        confirmText = 'Confirm',
        cancelText = 'Cancel',
    }) => {
        return new Promise((resolve) => {
            setDialog({
                isOpen: true,
                title,
                message,
                type,
                confirmText,
                cancelText,
                onConfirm: () => {
                    setDialog((prev) => ({ ...prev, isOpen: false }));
                    resolve(true);
                },
                onCancel: () => {
                    setDialog((prev) => ({ ...prev, isOpen: false }));
                    resolve(false);
                },
            });
        });
    };

    return (
        <ConfirmDialogContext.Provider value={{ confirm }}>
            {children}
            <ConfirmDialog {...dialog} />
        </ConfirmDialogContext.Provider>
    );
};

// The actual dialog component
const ConfirmDialog = ({
    isOpen,
    title,
    message,
    type,
    onConfirm,
    onCancel,
    confirmText,
    cancelText,
}) => {
    if (!isOpen) return null;

    const icons = {
        info: <Info className="w-6 h-6 text-black" />,
        success: <CheckCircle className="w-6 h-6 text-black" />,
        warning: <AlertTriangle className="w-6 h-6 text-black" />,
        danger: <AlertCircle className="w-6 h-6 text-black" />,
    };

    const buttonColors = {
        info: 'bg-black hover:bg-gray-800',
        success: 'bg-black hover:bg-gray-800',
        warning: 'bg-black hover:bg-gray-800',
        danger: 'bg-black hover:bg-gray-800',
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={onCancel}
            />

            {/* Dialog */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full animate-in fade-in zoom-in duration-200">
                {/* Close button */}
                <button
                    onClick={onCancel}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Content */}
                <div className="p-6">
                    {/* Icon and Title */}
                    <div className="flex items-start gap-4 mb-4">
                        <div className="flex-shrink-0 mt-1">{icons[type]}</div>
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                {title}
                            </h3>
                            <p className="text-gray-600 text-sm">{message}</p>
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 justify-end mt-6">
                        <button
                            onClick={onCancel}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            {cancelText}
                        </button>
                        <button
                            onClick={onConfirm}
                            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${buttonColors[type]}`}
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Demo Component - Inner component that uses the hook
function DemoContent() {
    const [selectedQueries] = useState(new Set([1, 2, 3, 4, 5]));
    const { confirm } = useConfirmDialog();

    const handleCloseQueries = async () => {
        const result = await confirm({
            title: 'Close Selected Queries',
            message: `Are you sure you want to close ${selectedQueries.size} selected queries?`,
            type: 'warning',
            confirmText: 'Close Queries',
            cancelText: 'Cancel',
        });

        if (result) {
            console.log('Queries closed!');
            alert('Queries have been closed!');
        } else {
            console.log('Action cancelled');
        }
    };

    const handleDelete = async () => {
        const result = await confirm({
            title: 'Delete Item',
            message: 'This action cannot be undone. Are you sure you want to delete this item?',
            type: 'danger',
            confirmText: 'Delete',
            cancelText: 'Cancel',
        });

        if (result) {
            alert('Item deleted!');
        }
    };

    const handleSave = async () => {
        const result = await confirm({
            title: 'Save Changes',
            message: 'Do you want to save your changes before leaving?',
            type: 'info',
            confirmText: 'Save',
            cancelText: "Don't Save",
        });

        if (result) {
            alert('Changes saved!');
        }
    };

    return (
        <ConfirmDialogProvider>
            <div className="min-h-screen bg-gray-50 p-8">
                <div className="max-w-2xl mx-auto">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Reusable Confirmation Dialog
                    </h1>
                    <p className="text-gray-600 mb-8">
                        Click the buttons below to see different dialog types
                    </p>

                    <div className="bg-white rounded-lg shadow-lg p-8 space-y-4">
                        <button
                            onClick={handleCloseQueries}
                            className="w-full px-6 py-3 bg-black hover:bg-gray-800 text-white font-medium rounded-lg transition-colors"
                        >
                            Close Selected Queries (Warning)
                        </button>

                        <button
                            onClick={handleDelete}
                            className="w-full px-6 py-3 bg-black hover:bg-gray-800 text-white font-medium rounded-lg transition-colors"
                        >
                            Delete Item (Danger)
                        </button>

                        <button
                            onClick={handleSave}
                            className="w-full px-6 py-3 bg-black hover:bg-gray-800 text-white font-medium rounded-lg transition-colors"
                        >
                            Save Changes (Info)
                        </button>
                    </div>

                    <div className="mt-8 bg-gray-900 text-gray-100 rounded-lg p-6">
                        <h2 className="text-lg font-semibold mb-3">Usage Example:</h2>
                        <pre className="text-sm overflow-x-auto">
                        </pre>
                    </div>
                </div>
            </div>
        </ConfirmDialogProvider>
    );
}